import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Loader2, Plus, Pencil, CheckCircle, XCircle, Save, X, Trash2, RefreshCw, Link2, GripVertical, ListOrdered } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { Input } from '@/components/ui/input';

const DEFAULTS = { key: '', label: '', sage_nominal_code: '4001', sort_order: 0, active: true };

function CategoryRow({ cat, dragHandleProps, draggableProvided, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    label: cat.label,
    sage_nominal_code: cat.sage_nominal_code || '4001',
    sort_order: cat.sort_order ?? 0,
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    // Sage nominal codes are 4 digits — anything else would post the category's
    // sales to a nominal that doesn't exist, so non-digits are dropped.
    const nominal = String(form.sage_nominal_code || '').replace(/\D/g, '').slice(0, 4);
    await base44.entities.Category.update(cat.id, {
      label: form.label,
      sort_order: Number(form.sort_order) || 0,
      sage_nominal_code: nominal || '4001',
    });
    setSaving(false);
    setEditing(false);
    onSaved();
  };

  const handleToggleActive = async () => {
    await base44.entities.Category.update(cat.id, { active: !cat.active });
    onSaved();
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete the "${cat.label}" category? Products already assigned to it will keep their value but will no longer appear under it in the shop.`)) return;
    await base44.entities.Category.delete(cat.id);
    onSaved();
  };

  return (
    <div
      ref={draggableProvided?.innerRef}
      {...(draggableProvided?.draggableProps || {})}
      className={`grid grid-cols-12 gap-4 px-6 py-4 border-b border-white/5 items-center transition-colors hover:bg-white/[0.02] min-w-[760px] ${!cat.active ? 'opacity-50' : ''} ${draggableProvided?.isDragging ? 'bg-primary/5' : ''}`}
    >
      {/* Drag handle */}
      <div className="col-span-1 flex items-center" {...(dragHandleProps || {})}>
        <GripVertical className="w-4 h-4 text-white/20 hover:text-white/60 cursor-grab active:cursor-grabbing" />
      </div>

      {/* Key */}
      <p className="col-span-2 text-white/40 text-xs font-mono truncate">{cat.key}</p>

      {/* Label */}
      <div className="col-span-3">
        {editing ? (
          <Input
            value={form.label}
            onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
            className="bg-[#0d0d0f] border-white/10 text-white h-8 text-sm placeholder:text-white/20 focus:border-primary/60"
          />
        ) : (
          <p className="text-white text-sm font-bold truncate">{cat.label}</p>
        )}
      </div>

      {/* Sage nominal */}
      <div className="col-span-2">
        {editing ? (
          <Input
            value={form.sage_nominal_code}
            onChange={e => setForm(f => ({ ...f, sage_nominal_code: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
            maxLength={4}
            inputMode="numeric"
            className="bg-[#0d0d0f] border-white/10 text-white h-8 text-sm w-24 font-mono focus:border-primary/60"
            placeholder="4001"
          />
        ) : (
          <p className="text-white/40 text-sm font-mono">{cat.sage_nominal_code || '4001'}</p>
        )}
      </div>

      {/* Sort order */}
      <div className="col-span-1">
        {editing ? (
          <Input
            type="number"
            value={form.sort_order}
            onChange={e => setForm(f => ({ ...f, sort_order: e.target.value }))}
            className="bg-[#0d0d0f] border-white/10 text-white h-8 text-sm w-20 focus:border-primary/60"
          />
        ) : (
          <p className="text-white/40 text-sm font-mono">{cat.sort_order ?? 0}</p>
        )}
      </div>

      {/* Square link + status */}
      <div className="col-span-1 flex flex-col gap-1">
        {cat.square_category_id ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-400/80" title={cat.square_category_id}>
            <Link2 className="w-3 h-3" /> LINKED
          </span>
        ) : (
          <span className="text-white/20 text-[10px]">—</span>
        )}
        <span className={`text-[10px] font-bold px-2 py-0.5 w-fit ${cat.active ? 'bg-primary/10 text-primary' : 'bg-white/5 text-white/30'}`}>
          {cat.active ? 'ON' : 'OFF'}
        </span>
      </div>

      {/* Actions */}
      <div className="col-span-2 flex items-center gap-1">
        {editing ? (
          <>
            <button onClick={handleSave} disabled={saving} className="p-2 text-primary border border-primary/20 hover:border-primary/60 transition-all" title="Save">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            </button>
            <button onClick={() => setEditing(false)} className="p-2 text-white/30 border border-white/5 hover:border-white/20 transition-all" title="Cancel">
              <X className="w-3.5 h-3.5" />
            </button>
          </>
        ) : (
          <>
            <button onClick={() => setEditing(true)} className="p-2 text-white/30 hover:text-white border border-white/5 hover:border-white/20 transition-all" title="Edit">
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button onClick={handleToggleActive} className={`p-2 border transition-all ${cat.active ? 'text-yellow-400 border-yellow-400/20 hover:border-yellow-400/60' : 'text-white/30 border-white/5 hover:border-white/20'}`} title={cat.active ? 'Deactivate' : 'Activate'}>
              {cat.active ? <XCircle className="w-3.5 h-3.5" /> : <CheckCircle className="w-3.5 h-3.5" />}
            </button>
            <button onClick={handleDelete} className="p-2 text-red-400/70 border border-red-400/20 hover:border-red-400/60 hover:text-red-400 transition-all" title="Delete">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function AddCategoryRow({ onSaved }) {
  const [form, setForm] = useState(DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');

  const handleCreate = async () => {
    if (!form.label) { setError('Label is required.'); return; }
    setSaving(true);
    setError('');
    try {
      const res = await base44.functions.invoke('createSquareCategory', {
        label: form.label,
        key: form.key || undefined,
        sage_nominal_code: String(form.sage_nominal_code || '').replace(/\D/g, '').slice(0, 4) || '4001',
        sort_order: Number(form.sort_order) || 0,
        active: form.active,
      });
      if (!res?.data?.success) throw new Error(res?.data?.error || 'Failed to create category');
      setForm(DEFAULTS);
      setOpen(false);
      onSaved();
    } catch (e) {
      setError(e.message || 'Failed to create category');
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="flex items-center gap-2 text-xs font-bold text-white/40 hover:text-white border border-dashed border-white/10 hover:border-white/30 px-4 py-3 w-full transition-all">
        <Plus className="w-3.5 h-3.5" /> ADD CATEGORY
      </button>
    );
  }

  return (
    <div className="border border-primary/20 bg-primary/5 px-6 py-4 space-y-3">
      <p className="text-primary text-[10px] font-bold tracking-widest">NEW CATEGORY</p>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <div>
          <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1">KEY (stable, no spaces)</p>
          <Input value={form.key} onChange={e => setForm(f => ({ ...f, key: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
            className="bg-[#0d0d0f] border-white/10 text-white h-9 text-sm focus:border-primary/60" placeholder="sim_rigs" />
        </div>
        <div>
          <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1">LABEL</p>
          <Input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
            className="bg-[#0d0d0f] border-white/10 text-white h-9 text-sm focus:border-primary/60" placeholder="SIM RIGS" />
        </div>
        <div>
          <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1">SAGE NOMINAL (4-DIGIT)</p>
          <Input value={form.sage_nominal_code} onChange={e => setForm(f => ({ ...f, sage_nominal_code: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
            maxLength={4} inputMode="numeric"
            className="bg-[#0d0d0f] border-white/10 text-white h-9 text-sm font-mono focus:border-primary/60" placeholder="4001" />
        </div>
        <div>
          <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1">SORT ORDER</p>
          <Input type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: e.target.value }))}
            className="bg-[#0d0d0f] border-white/10 text-white h-9 text-sm focus:border-primary/60" placeholder="0" />
        </div>
      </div>
      {error && <p className="text-red-400 text-xs">{error}</p>}
      <div className="flex gap-2">
        <button onClick={handleCreate} disabled={saving}
          className="flex items-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-50 text-white font-bold text-xs tracking-wider px-4 py-2 transition-colors">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          CREATE
        </button>
        <button onClick={() => { setOpen(false); setError(''); }}
          className="border border-white/10 hover:border-white/25 text-white/40 hover:text-white text-xs font-bold px-4 py-2 transition-all">
          CANCEL
        </button>
      </div>
    </div>
  );
}

export default function CategoryManager() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [syncing, setSyncing] = useState(false);
  const [renumbering, setRenumbering] = useState(false);
  const [localCats, setLocalCats] = useState([]);

  const { data: categories = [], isLoading } = useQuery({
    queryKey: ['admin-categories'],
    queryFn: () => base44.entities.Category.list('sort_order'),
  });

  // Mirror the server list into local state so drag-and-drop can reorder
  // optimistically without waiting for the query to refetch.
  useEffect(() => {
    setLocalCats(categories);
  }, [categories]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-categories'] });
    queryClient.invalidateQueries({ queryKey: ['categories'] });
  };

  const persistOrder = async (ordered) => {
    // Renumber every category 0..n so stray "999" sort values are normalised.
    const updates = ordered.map((c, i) => ({ id: c.id, sort_order: i }));
    await base44.entities.Category.bulkUpdate(updates);
  };

  const onDragEnd = async (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    const reordered = Array.from(localCats);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setLocalCats(reordered); // optimistic
    try {
      await persistOrder(reordered);
      invalidate();
    } catch (e) {
      toast({ title: 'Reorder failed', description: e.message, variant: 'destructive' });
      setLocalCats(categories); // revert
    }
  };

  const handleRenumber = async () => {
    setRenumbering(true);
    try {
      await persistOrder(localCats);
      invalidate();
      toast({ title: 'Order renumbered', description: 'All sort orders reset to 0, 1, 2… in display order.' });
    } catch (e) {
      toast({ title: 'Renumber failed', description: e.message, variant: 'destructive' });
    } finally {
      setRenumbering(false);
    }
  };

  const handleSyncFromSquare = async () => {
    setSyncing(true);
    try {
      const res = await base44.functions.invoke('syncSquareCategories', {});
      const data = res?.data;
      if (!data?.success) throw new Error(data?.error || 'Sync failed');
      toast({
        title: 'Categories synced from Square',
        description: `${data.stats.created} created · ${data.stats.updated} updated · ${data.stats.skipped} already linked`,
      });
      invalidate();
    } catch (e) {
      toast({ title: 'Sync failed', description: e.message, variant: 'destructive' });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="border border-white/5 overflow-x-auto">
      {/* Sync bar */}
      <div className="flex items-center justify-between px-6 py-3 bg-[#111115] border-b border-white/5 gap-3 flex-wrap">
        <p className="text-white/40 text-xs">
          Drag rows to reorder. Categories are kept in sync with Square both ways. New Square categories arrive here hidden until enabled; new categories created here push to Square instantly.
          <span className="block mt-1 text-white/30">
            Sage nominals: 4000 rigs &amp; PCs · 4001 webstore components · 4002 POS hardware (applied automatically to in-venue sales) · 4003 bookings &amp; experiences · 4007 food &amp; drink.
          </span>
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={handleRenumber}
            disabled={renumbering}
            className="flex items-center gap-2 border border-white/10 hover:border-white/30 disabled:opacity-50 text-white/60 hover:text-white font-bold text-xs tracking-wider px-4 py-2 transition-colors"
            title="Reset all sort orders to 0, 1, 2… in the current display order (clears stray 999 values)"
          >
            {renumbering ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ListOrdered className="w-3.5 h-3.5" />}
            RENUMBER
          </button>
          <button
            onClick={handleSyncFromSquare}
            disabled={syncing}
            className="flex items-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-50 text-white font-bold text-xs tracking-wider px-4 py-2 transition-colors"
          >
            {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            SYNC FROM SQUARE
          </button>
        </div>
      </div>

      {/* Header row */}
      <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-[#0d0d0f] border-b border-white/5 min-w-[760px]">
        <p className="col-span-1" />
        <p className="col-span-2 text-white/30 text-[10px] font-bold tracking-widest">KEY</p>
        <p className="col-span-3 text-white/30 text-[10px] font-bold tracking-widest">LABEL</p>
        <p className="col-span-2 text-white/30 text-[10px] font-bold tracking-widest">SAGE NOMINAL</p>
        {/* 4-digit nominal code (4000/4001/4002/4003/4007) */}
        <p className="col-span-1 text-white/30 text-[10px] font-bold tracking-widest">ORDER</p>
        <p className="col-span-1 text-white/30 text-[10px] font-bold tracking-widest">STATUS</p>
        <p className="col-span-2 text-white/30 text-[10px] font-bold tracking-widest">ACTIONS</p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
        </div>
      ) : localCats.length === 0 ? (
        <div className="text-center py-10 text-white/30 text-sm">No categories yet. Sync from Square to import them.</div>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="categories">
            {(dropProvided) => (
              <div ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                {localCats.map((cat, index) => (
                  <Draggable key={cat.id} draggableId={cat.id} index={index}>
                    {(dragProvided) => (
                      <CategoryRow
                        cat={cat}
                        draggableProvided={dragProvided}
                        dragHandleProps={dragProvided.dragHandleProps}
                        onSaved={invalidate}
                      />
                    )}
                  </Draggable>
                ))}
                {dropProvided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      <div className="p-4">
        <AddCategoryRow onSaved={invalidate} />
      </div>
    </div>
  );
}